import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import TornaAlMenu from "../components/TornaAlMenu";
import { authFetch } from "../utils/authFetch";

const API_URL = process.env.REACT_APP_API_URL || "";
const CART_KEY = "dzr_app_cart";

const money = (v) => `€${Number(v || 0).toFixed(2)}`;

const formatDateIT = (value) => {
  if (!value) return "";
  const d = new Date(`${value}T12:00:00`);
  return d.toLocaleDateString("it-IT", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const formatSlot = (value) => String(value || "").replace("-", " - ");

export default function AppOfferCartPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const offerId = params.get("offer_id");
  const productId = params.get("product_id");

  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("WALLET");
  const [pickupDate, setPickupDate] = useState(new Date().toISOString().slice(0, 10));
  const [pickupSlot, setPickupSlot] = useState("16:00-19:00");
  const [customerNote, setCustomerNote] = useState("");
  const [wallet, setWallet] = useState(null);
  const [successOrder, setSuccessOrder] = useState(null);

  const [deliveryConfig, setDeliveryConfig] = useState(null);
  const [fulfillmentType, setFulfillmentType] = useState("PICKUP");
  const [deliveryRecipient, setDeliveryRecipient] = useState("");
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [deliveryCap, setDeliveryCap] = useState("");
  const [deliveryCity, setDeliveryCity] = useState("");
  const [deliveryProvince, setDeliveryProvince] = useState("");
  const [deliveryPhone, setDeliveryPhone] = useState("");
  const [deliveryNote, setDeliveryNote] = useState("");

  const checkoutRef = useRef(null);

  const saveCart = (next) => {
    setCart(next);
    localStorage.setItem(CART_KEY, JSON.stringify(next));
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setErr("");

      try {
        let current = JSON.parse(localStorage.getItem(CART_KEY) || "[]");

        if (offerId) {
          const res = localStorage.getItem("access")
            ? await authFetch(`${API_URL}/api/app/offers/`)
            : await fetch(`${API_URL}/api/app/offers/`);
          const data = await res.json();
          if (!res.ok) throw new Error(data?.detail || "Errore caricamento offerta");

          const list = Array.isArray(data) ? data : [];
          const selected = list.find((x) => String(x.id) === String(offerId));

          if (selected) {
            const exists = current.find((x) => String(x.id) === String(selected.id));
            current = exists
              ? current.map((x) =>
                  String(x.id) === String(selected.id)
                    ? { ...x, qty: Number(x.qty || 1) + 1 }
                    : x
                )
              : [...current, {
                    ...selected,
                    qty: 1,
                    type: "offer",
                    product_id: selected.product,
                    cart_id: `offer-${selected.id}`,
                  }];

            localStorage.setItem(CART_KEY, JSON.stringify(current));
            navigate("/app/carrello", { replace: true });
          }
        }


          if (productId) {
            const res = localStorage.getItem("access")
              ? await authFetch(`${API_URL}/api/app/products/`)
              : await fetch(`${API_URL}/api/app/products/`);
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || "Errore caricamento prodotto");

            const list = Array.isArray(data) ? data : [];
            const selected = list.find((x) => String(x.id) === String(productId));

            if (selected) {
              const status = String(selected.availability_status || "AVAILABLE").toUpperCase();
              if (status === "COMING_SOON" || status === "UNAVAILABLE") {
                setErr(status === "COMING_SOON"
                  ? "Questo prodotto è in arrivo e non è ancora ordinabile."
                  : "Questo prodotto non è attualmente disponibile."
                );
                navigate("/app/products", { replace: true });
                return;
              }

              const item = {
                id: selected.id,
                cart_id: `product-${selected.id}`,
                type: "product",
                code: selected.category || "PRODOTTO",
                title: selected.name,
                subtitle: selected.unit_type === "litri"
                  ? `${selected.litri_per_unita || "0"} L`
                  : `${selected.pezzi_per_unita || 1} pz`,
                price: selected.prezzo,
                product_id: selected.id,
              };

              const exists = current.find((x) =>
                String(x.cart_id || `${x.type || "offer"}-${x.id}`) === item.cart_id
              );

              current = exists
                ? current.map((x) =>
                    String(x.cart_id || `${x.type || "offer"}-${x.id}`) === item.cart_id
                      ? { ...x, qty: Number(x.qty || 1) + 1 }
                      : x
                  )
                : [...current, { ...item, qty: 1 }];

              localStorage.setItem(CART_KEY, JSON.stringify(current));
              navigate("/app/carrello", { replace: true });
            }
          }

        setCart(current);

        try {
          const deliveryRes = await fetch(`${API_URL}/api/app/delivery-config/`);
          const deliveryData = await deliveryRes.json().catch(() => ({}));

          if (deliveryRes.ok) {
            setDeliveryConfig(deliveryData);
          } else {
            throw new Error("Configurazione consegna non disponibile");
          }
        } catch {
          setDeliveryConfig({
            is_enabled: false,
            min_order_amount: "25.00",
            max_radius_km: "5.00",
            surcharge_percent: "10.00",
          });
        }

        if (localStorage.getItem("access")) {
          const walletRes = await authFetch(`${API_URL}/api/app/wallet/`);
          const walletData = await walletRes.json().catch(() => ({}));
          if (walletRes.ok) {
            setWallet(walletData);
          }
        }
      } catch (e) {
        setErr(e.message || "Errore");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [offerId, productId, navigate]);

  useEffect(() => {
    const stripeOrder = params.get("stripe_order");
    const sessionId = params.get("session_id");

    if (!["success", "success_cart"].includes(stripeOrder) || !sessionId) return;

    const confirmStripeOrder = async () => {
      setSubmitting(true);
      setErr("");

      try {
        const res = await authFetch(stripeOrder === "success_cart" ? `${API_URL}/api/app/ordini/stripe/cart/confirm/` : `${API_URL}/api/app/ordini/stripe/confirm/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ session_id: sessionId }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.detail || "Pagamento Stripe non confermato.");
        }

        localStorage.removeItem(CART_KEY);
        setCart([]);
        setCheckoutOpen(false);
        const stripeFulfillmentType =
          stripeOrder === "success_cart"
            ? data?.fulfillment_type || "PICKUP"
            : "PICKUP";

        const stripeIsDelivery = stripeFulfillmentType === "DELIVERY";

        setSuccessOrder({
          orderId: data?.order_id,
          total: data?.amount,
          paymentMethod: "STRIPE",
          fulfillmentType: stripeFulfillmentType,
          pickupDate: "",
          pickupSlot: "",
          deliveryAddress: stripeIsDelivery
            ? [
                data?.delivery_address,
                [data?.delivery_cap, data?.delivery_city].filter(Boolean).join(" "),
                data?.delivery_province ? `(${data.delivery_province})` : "",
              ]
                .filter(Boolean)
                .join(", ")
            : "",
          deliveryFee: stripeIsDelivery ? data?.delivery_fee_amount : "0.00",
        });

        navigate("/app/carrello", { replace: true });
      } catch (e) {
        setErr(e.message || "Errore durante la conferma del pagamento Stripe.");
      } finally {
        setSubmitting(false);
      }
    };

    confirmStripeOrder();
  }, [location.search, navigate]);


  const getCartId = (x) => x.cart_id || `${x.type || "offer"}-${x.id}`;

  const updateQty = (cartId, qty) => {
    const q = Math.max(1, Number(qty || 1));
    saveCart(
      cart.map((x) =>
        String(getCartId(x)) === String(cartId)
          ? { ...x, qty: q }
          : x
      )
    );
  };

  const removeItem = (cartId) => {
    saveCart(
      cart.filter((x) =>
        String(getCartId(x)) !== String(cartId)
      )
    );
  };

  const total = cart.reduce((sum, x) => sum + Number(x.price || 0) * Number(x.qty || 1), 0);

  const deliveryEnabled = Boolean(deliveryConfig?.is_enabled);
  const deliveryMinOrder = Number(deliveryConfig?.min_order_amount || 25);
  const deliverySurchargePercent = Number(deliveryConfig?.surcharge_percent || 0);
  const deliveryMaxRadiusKm = Number(deliveryConfig?.max_radius_km || 0);

  const deliveryMinimumReached = total >= deliveryMinOrder;
  const isDelivery = fulfillmentType === "DELIVERY";

  const deliveryFee = isDelivery
    ? Number((total * deliverySurchargePercent / 100).toFixed(2))
    : 0;

  const finalTotal = Number((total + deliveryFee).toFixed(2));

  const walletBalance = Number(wallet?.total_balance || 0);
  const walletInsufficient =
    paymentMethod === "WALLET" &&
    wallet &&
    walletBalance < Number(finalTotal || 0);

  const isSfusoItem = (x) => String(x.code || "").toUpperCase().includes("SFUSO");
  const isContenitoreItem = (x) => {
    const code = String(x.code || "").toUpperCase();
    const title = String(x.title || x.name || "").toUpperCase();
    return (
      code === "CONTENITORI" ||
      code === "OGGETTISTICA" ||
      title.includes("CONTENITOR") ||
      title.includes("BOTTIGL") ||
      title.includes("TAPPO") ||
      title.includes("DAMA")
    );
  };
  const hasSfuso = cart.some(isSfusoItem);
  const hasContenitore = cart.some(isContenitoreItem);
  const missingContenitore = hasSfuso && !hasContenitore;

  const confirmOrder = async () => {
    if (!localStorage.getItem("access")) {
      navigate("/app/login", { state: { returnTo: "/app/carrello" } });
      return;
    }

    setErr("");

    const items = cart
        .filter((x) => x.product_id)
      .map((x) => ({
        product: x.product_id,
        quantity: String(Number(x.qty || 1)),
        formato: x.subtitle || "",
        contenitore: x.contenitore || "",
      }));

    if (!items.length) {
      setErr("Il carrello non contiene prodotti ordinabili.");
      return;
    }

    if (paymentMethod === "WALLET" && walletInsufficient) {
      setErr("Credito Wallet insufficiente. Scegli un altro metodo di pagamento o ricarica il credito.");
      return;
    }

    if (isDelivery) {
      if (!deliveryEnabled) {
        setErr("La consegna a domicilio non è attualmente disponibile.");
        return;
      }

      if (!deliveryMinimumReached) {
        setErr(`Per la consegna a domicilio l'ordine minimo è di ${money(deliveryMinOrder)}.`);
        return;
      }

      if (
        !deliveryRecipient.trim() ||
        !deliveryAddress.trim() ||
        !deliveryCap.trim() ||
        !deliveryCity.trim() ||
        !deliveryProvince.trim() ||
        !deliveryPhone.trim()
      ) {
        setErr("Per la consegna a domicilio compila tutti i campi richiesti.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const [pickupTimeFrom, pickupTimeTo] = pickupSlot.split("-");

      const payload = isDelivery
        ? {
            items,
            fulfillment_type: "DELIVERY",
            delivery_recipient: deliveryRecipient.trim(),
            delivery_address: deliveryAddress.trim(),
            delivery_cap: deliveryCap.trim(),
            delivery_city: deliveryCity.trim(),
            delivery_province: deliveryProvince.trim().toUpperCase(),
            delivery_phone: deliveryPhone.trim(),
            delivery_notes: deliveryNote.trim(),
            payment_method: paymentMethod,
          }
        : {
            items,
            fulfillment_type: "PICKUP",
            customer_note: customerNote.trim() || "Ordine da app cliente",
            pickup_date: pickupDate,
            pickup_time_from: pickupTimeFrom,
            pickup_time_to: pickupTimeTo,
            payment_method: paymentMethod,
          };

      if (paymentMethod === "WALLET") {
        const walletRes = await authFetch(`${API_URL}/api/app/ordini/wallet/checkout/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const walletData = await walletRes.json().catch(() => ({}));

        if (!walletRes.ok) {
          throw new Error(walletData?.detail || "Pagamento wallet non riuscito.");
        }

        localStorage.removeItem(CART_KEY);
        setCart([]);
        setCheckoutOpen(false);
        setSuccessOrder({
          orderId: walletData?.order_id,
          total: walletData?.amount || finalTotal,
          paymentMethod: "WALLET",
          fulfillmentType,
          pickupDate: isDelivery ? "" : pickupDate,
          pickupSlot: isDelivery ? "" : pickupSlot,
          deliveryAddress: isDelivery
            ? `${deliveryAddress.trim()}, ${deliveryCap.trim()} ${deliveryCity.trim()} (${deliveryProvince.trim().toUpperCase()})`
            : "",
        });
        return;
      }

      if (paymentMethod === "STRIPE") {
        const stripeRes = await authFetch(`${API_URL}/api/app/ordini/stripe/cart/create-session/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const stripeData = await stripeRes.json().catch(() => ({}));

        if (!stripeRes.ok) {
          throw new Error(stripeData?.detail || "Errore creazione pagamento Stripe.");
        }

        if (!stripeData?.url) {
          throw new Error("Stripe non ha restituito il link di pagamento.");
        }

        window.location.href = stripeData.url;
        return;
      }

      setErr("Metodo di pagamento non valido.");
      return;
    } catch (e) {
      setErr(e.message || "Errore durante la conferma ordine.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-4">
      <TornaAlMenu to="/" />

      {successOrder && (
        <div className="card border-0 rounded-5 shadow-lg mb-4 overflow-hidden">
          <div className="card-body p-4" style={{ background: "linear-gradient(135deg, #e9fff0 0%, #ffffff 100%)" }}>
            <div className="d-flex align-items-start gap-3">
              <div className="display-5">✅</div>
              <div className="flex-grow-1">
                <div className="text-success small fw-bold text-uppercase">Pagamento effettuato</div>
                <h2 className="fw-bold mb-2">Ordine inviato correttamente</h2>
                <div className="text-muted mb-3">Il pagamento è stato effettuato correttamente e il tuo ordine è stato inviato al punto vendita.</div>
                <div className="border rounded-4 p-3 mb-3 bg-white">
                  <div className="d-flex justify-content-between py-1"><span className="text-muted">Importo pagato</span><strong>{money(successOrder.total)}</strong></div>
                  <div className="d-flex justify-content-between py-1"><span className="text-muted">Metodo</span><strong>{successOrder.paymentMethod === "STRIPE" ? "Carta / Apple Pay / Google Pay" : "Wallet / Credito App"}</strong></div>
                  {successOrder.fulfillmentType === "DELIVERY" ? (
                    <div className="py-1">
                      <div className="text-muted mb-1">Consegna a domicilio</div>
                      {successOrder.deliveryAddress && (
                        <strong className="d-block">{successOrder.deliveryAddress}</strong>
                      )}
                    </div>
                  ) : (
                    <div className="d-flex justify-content-between align-items-start gap-3 py-1">
                      <span className="text-muted">Ritiro</span>
                      <strong className="text-end">
                        {formatDateIT(successOrder.pickupDate)}
                        <br />
                        {formatSlot(successOrder.pickupSlot)}
                      </strong>
                    </div>
                  )}
                </div>
                <div className="d-flex flex-wrap gap-2">
                  <button type="button" className="btn btn-success rounded-pill px-4" onClick={() => navigate("/app/ordini")}>Vai ai miei ordini</button>
                  <button type="button" className="btn btn-outline-secondary rounded-pill px-4" onClick={() => navigate("/app/prodotti")}>Torna al catalogo</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


          <div
            className="card border-0 rounded-5 shadow-lg mb-3 overflow-hidden"
            style={{ background: "linear-gradient(135deg, #5b0f1b 0%, #8b1e2d 55%, #211014 100%)" }}
          >
            <div className="card-body p-4 d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <div className="text-white-50 small">App Clienti</div>
                <h2 className="m-0 text-white fw-bold">🛒 Il tuo carrello</h2>
                <div className="text-white-50 small mt-1">
                  Controlla i prodotti prima del checkout.
                </div>
              </div>

              <button
                className="btn rounded-pill px-4 py-2 fw-bold"
                style={{ background: "#fff8f1", color: "#5b0f1b", border: 0 }}
                onClick={() => navigate("/app/products")}
              >
                + Continua acquisti
              </button>
            </div>
          </div>

      {loading ? (
        <p className="text-muted">Caricamento…</p>
      ) : err ? (
        <div className="alert alert-danger">{err}</div>
      ) : cart.length === 0 ? (
        <div className="alert alert-warning">Il carrello è vuoto.</div>
      ) : (
        <>
          <div className="row g-3">
            {cart.map((item) => (
              <div className="col-12" key={item.cart_id || `${item.type || "offer"}-${item.id}`}>
                <div className="card shadow-sm border-0 rounded-4">
                  <div className="card-body p-4">
                    <div className="row g-3 align-items-center">
                      <div className="col-12 col-md-2">
                        {item.image_url && (
                          <img
                            src={item.image_url}
                            alt={item.title}
                            className="img-fluid rounded-4"
                            style={{ maxHeight: 130, objectFit: "contain", width: "100%" }}
                          />
                        )}
                      </div>

                      <div className="col-12 col-md-5">
                        <div className="text-muted small">{item.code || "OFFERTA"}</div>
                        <h4 className="mb-1">{item.title}</h4>
                        {item.subtitle && <div className="text-muted">{item.subtitle}</div>}
                      </div>

                      <div className="col-6 col-md-2">
                        <label className="form-label small">Quantità</label>
                        <div className="d-flex align-items-center gap-2">
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-circle fw-bold"
                            style={{ width: 38, height: 38 }}
                            onClick={() => updateQty(item.cart_id || `${item.type || "offer"}-${item.id}`, Number(item.qty || 1) - 1)}
                            disabled={Number(item.qty || 1) <= 1}
                          >
                            −
                          </button>
                          <div className="form-control text-center fw-bold" style={{ minWidth: 54, background: "#fff" }}>
                            {Number(item.qty || 1)}
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-success rounded-circle fw-bold"
                            style={{ width: 38, height: 38 }}
                            onClick={() => updateQty(item.cart_id || `${item.type || "offer"}-${item.id}`, Number(item.qty || 1) + 1)}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="col-6 col-md-2">
                        <div className="text-muted small">Totale</div>
                        <div className="fs-4 fw-bold">
                          {money(Number(item.price || 0) * Number(item.qty || 1))}
                        </div>
                      </div>

                      <div className="col-12 col-md-1 text-end">
                        <button className="btn btn-outline-danger" onClick={() => removeItem(item.cart_id || `${item.type || "offer"}-${item.id}`)}>
                          X
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

            <div
              className="card border-0 rounded-5 mt-3 overflow-hidden"
              style={{ boxShadow: "0 16px 38px rgba(91,15,27,0.12)" }}
            >
              <div
                className="card-body p-4 d-flex justify-content-between align-items-center gap-3"
                style={{ background: "linear-gradient(135deg, #fff8f1 0%, #ffffff 100%)" }}
              >
                <div>
                  <div className="text-muted small">Totale carrello</div>
                  <div className="display-5 fw-bold" style={{ color: "#5b0f1b" }}>{money(total)}</div>
                </div>

                <button
                  className="btn btn-lg rounded-pill px-4 fw-bold"
                  style={{ background: "#5b0f1b", color: "#fff", border: 0, boxShadow: "0 10px 24px rgba(91,15,27,0.25)" }}
                  onClick={() => {
                    setErr("");
                    setCheckoutOpen(true);
                  
                    setTimeout(() => {
                      checkoutRef.current?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                      });
                    }, 150);
                  }}
                  disabled={submitting}
                >
                  Checkout
                </button>
              </div>
            </div>

          {checkoutOpen && (
            <div
              ref={checkoutRef}
              className="card shadow-sm border-0 rounded-4 mt-3"
            >
              <div className="card-body p-4">
                <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                  <div>
                    <div className="text-muted small">Checkout</div>
                    <h3 className="mb-1">Riepilogo ordine</h3>
                    <div className="text-muted">Controlla il carrello e scegli come pagare.</div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => setCheckoutOpen(false)}
                    disabled={submitting || missingContenitore}
                  >
                    Modifica carrello
                  </button>
                </div>

                <div className="mb-4">
                  <div className="fw-bold mb-2">Come vuoi ricevere l'ordine?</div>

                  <div className="row g-2">
                    <div className="col-12 col-md-6">
                      <label className="d-block border rounded-4 p-3 h-100">
                        <input
                          type="radio"
                          className="form-check-input me-2"
                          checked={fulfillmentType === "PICKUP"}
                          onChange={() => setFulfillmentType("PICKUP")}
                        />
                        <span className="fw-semibold">Ritiro in negozio</span>
                        <div className="small text-muted ms-4">
                          Ritira gratuitamente il tuo ordine da Dallo Zio Roby.
                        </div>
                      </label>
                    </div>

                    {deliveryEnabled && (
                      <div className="col-12 col-md-6">
                        <label
                          className={`d-block border rounded-4 p-3 h-100 ${
                            !deliveryMinimumReached ? "opacity-50" : ""
                          }`}
                        >
                          <input
                            type="radio"
                            className="form-check-input me-2"
                            checked={fulfillmentType === "DELIVERY"}
                            disabled={!deliveryMinimumReached}
                            onChange={() => setFulfillmentType("DELIVERY")}
                          />
                          <span className="fw-semibold">Consegna a domicilio</span>

                          <div className="small text-muted ms-4">
                            Entro {deliveryMaxRadiusKm.toFixed(2)} km dal negozio · supplemento{" "}
                            {deliverySurchargePercent.toFixed(2)}%.
                          </div>

                          {!deliveryMinimumReached && (
                            <div className="small text-danger fw-semibold ms-4 mt-1">
                              Ordine minimo {money(deliveryMinOrder)}. Mancano{" "}
                              {money(Math.max(0, deliveryMinOrder - total))}.
                            </div>
                          )}
                        </label>
                      </div>
                    )}
                  </div>

                  {!deliveryEnabled && (
                    <div className="small text-muted mt-2">
                      La consegna a domicilio non è attualmente disponibile.
                    </div>
                  )}
                </div>

                {isDelivery ? (
                  <div className="row g-3 mb-3">
                    <div className="col-12">
                      <div className="alert alert-info rounded-4 mb-0">
                        Inserisci l'indirizzo di consegna. La disponibilità verrà verificata
                        in base alla distanza dal negozio.
                      </div>
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Destinatario</label>
                      <input
                        type="text"
                        className="form-control"
                        value={deliveryRecipient}
                        onChange={(e) => setDeliveryRecipient(e.target.value)}
                        placeholder="Nome e cognome"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Telefono</label>
                      <input
                        type="tel"
                        className="form-control"
                        value={deliveryPhone}
                        onChange={(e) => setDeliveryPhone(e.target.value)}
                        placeholder="Numero di telefono"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Via e numero civico</label>
                      <input
                        type="text"
                        className="form-control"
                        value={deliveryAddress}
                        onChange={(e) => setDeliveryAddress(e.target.value)}
                        placeholder="Esempio: Via Roma 10"
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">CAP</label>
                      <input
                        type="text"
                        className="form-control"
                        value={deliveryCap}
                        onChange={(e) => setDeliveryCap(e.target.value)}
                        placeholder="21052"
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Città</label>
                      <input
                        type="text"
                        className="form-control"
                        value={deliveryCity}
                        onChange={(e) => setDeliveryCity(e.target.value)}
                        placeholder="Busto Arsizio"
                      />
                    </div>

                    <div className="col-12 col-md-3">
                      <label className="form-label fw-semibold">Provincia</label>
                      <input
                        type="text"
                        className="form-control"
                        value={deliveryProvince}
                        onChange={(e) => setDeliveryProvince(e.target.value.toUpperCase())}
                        maxLength="2"
                        placeholder="VA"
                      />
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Note per la consegna</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={deliveryNote}
                        onChange={(e) => setDeliveryNote(e.target.value)}
                        placeholder="Esempio: citofono Rossi, secondo piano..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Giorno ritiro</label>
                      <input
                        type="date"
                        className="form-control"
                        value={pickupDate}
                        onChange={(e) => setPickupDate(e.target.value)}
                        min={new Date().toISOString().slice(0, 10)}
                      />
                    </div>

                    <div className="col-12 col-md-6">
                      <label className="form-label fw-semibold">Fascia oraria</label>
                      <select
                        className="form-select"
                        value={pickupSlot}
                        onChange={(e) => setPickupSlot(e.target.value)}
                      >
                        <option value="10:00-12:30">Mattina 10:00 - 12:30</option>
                        <option value="16:00-19:00">Pomeriggio 16:00 - 19:00</option>
                        <option value="18:00-19:30">Sera 18:00 - 19:30</option>
                      </select>
                    </div>

                    <div className="col-12">
                      <label className="form-label fw-semibold">Note per il ritiro</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={customerNote}
                        onChange={(e) => setCustomerNote(e.target.value)}
                        placeholder="Esempio: passo verso le 18, preparare in cartone, chiamatemi se serve..."
                      />
                    </div>
                  </div>
                )}

                <div className="border rounded-4 p-3 mb-3">
                  {cart.map((item) => (
                    <div
                      className="d-flex justify-content-between gap-3 py-2 border-bottom"
                      key={`checkout-${item.cart_id || `${item.type || "offer"}-${item.id}`}`}
                    >
                      <div>
                        <div className="fw-semibold">{item.title}</div>
                        <div className="small text-muted">Quantità: {Number(item.qty || 1)}</div>
                      </div>
                      <div className="fw-bold text-end">
                        {money(Number(item.price || 0) * Number(item.qty || 1))}
                      </div>
                    </div>
                  ))}
                  <div className="pt-3">
                    <div className="d-flex justify-content-between align-items-center py-1">
                      <span className="text-muted">Prodotti</span>
                      <strong>{money(total)}</strong>
                    </div>

                    {isDelivery && (
                      <div className="d-flex justify-content-between align-items-center py-1">
                        <span className="text-muted">
                          Consegna a domicilio ({deliverySurchargePercent.toFixed(2)}%)
                        </span>
                        <strong>{money(deliveryFee)}</strong>
                      </div>
                    )}

                    <div className="d-flex justify-content-between align-items-center pt-2 mt-2 border-top">
                      <div className="fw-bold">Totale da pagare</div>
                      <div className="fs-3 fw-bold">{money(finalTotal)}</div>
                    </div>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="fw-bold mb-2">Metodo di pagamento</div>

                    <label className="d-block border rounded-4 p-3 mb-2">
                      <input
                        type="radio"
                        className="form-check-input me-2"
                        checked={paymentMethod === "WALLET"}
                        onChange={() => setPaymentMethod("WALLET")}
                      />
                      <span className="fw-semibold">Credito App / Wallet</span>

                      <div className="small text-muted ms-4">
                        Saldo disponibile:
                        <strong> {money(wallet?.total_balance || 0)}</strong>
                      </div>

                      <button
                        type="button"
                        className="btn btn-outline-success btn-sm ms-4 mt-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate("/app/wallet");
                        }}
                      >
                        💰 Ricarica Wallet
                      </button>

                      <div className="small text-muted ms-4">
                        Credito normale: {money(wallet?.normal_balance || 0)}
                        {" • "}
                        Promo: {money(wallet?.promo_balance || 0)}
                      </div>

                      <div className="small text-muted ms-4">
                        Scala il credito disponibile e invia subito l'ordine.
                      </div>

                      {wallet && walletInsufficient && (
                        <div className="text-danger fw-bold ms-4 mt-2" style={{ fontSize: 18 }}>
                          ⚠️ Credito insufficiente: ti mancano {money(Number(finalTotal || 0) - walletBalance)}.
                        </div>
                      )}

                      {wallet && !walletInsufficient && (
                        <div className="small text-success ms-4 mt-1">
                          Credito sufficiente per pagare questo ordine.
                        </div>
                      )}
                    </label>

                    <label className="d-block border rounded-4 p-3 mb-2">
                      <input
                        type="radio"
                        className="form-check-input me-2"
                        checked={paymentMethod === "STRIPE"}
                        onChange={() => setPaymentMethod("STRIPE")}
                      />
                      <span className="fw-semibold">Carta / Apple Pay / Google Pay</span>
                      <div className="small text-muted ms-4">Pagamento online sicuro tramite Stripe Checkout.</div>
                    </label>

                </div>

                <button
                  type="button"
                  className="btn btn-success btn-lg w-100"
                  onClick={confirmOrder}
                  disabled={submitting || (paymentMethod === "WALLET" && walletInsufficient)}
                >
                  {submitting ? "Pagamento in corso..." : paymentMethod === "STRIPE" ? "Confermo e paga online" : "Confermo e pago con Credito App"}
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
